import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import { Color, Label } from 'ng2-charts';
import { FormControl, FormGroup } from '@angular/forms';
import { IOrganisation } from '@bciti/types';
import { OrganisationService } from 'src/app/services/organisation/organisation.service';
import { CollectsAiAnalyticsBase, UsageAnalytics, SessionAnalytics, PerformanceAnalytics, FeedbackAnalytics } from 'src/app/services/analytics/collects-ai/analytics.service';

@Component({
    selector: 'app-analytics',
    templateUrl: './analytics.component.html',
    styleUrls: ['./analytics.component.scss'],
})
export class CollectsAiAnalytics implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    // Organisation selector
    organisations: IOrganisation[] = [];
    selectedOrg = new FormControl();
    isLoadingOrgs = true;

    // Data loading
    isLoading = false;
    dataLoaded = false;

    // Date filter
    dateRange = new FormGroup({
        start: new FormControl(),
        end: new FormControl(),
    });

    // Data
    usageData: UsageAnalytics;
    sessionData: SessionAnalytics;
    performanceData: PerformanceAnalytics;
    feedbackData: FeedbackAnalytics;

    // Line Chart - Usage Trends
    usageLineChartData: ChartDataSets[] = [];
    usageLineChartLabels: Label[] = [];
    usageLineChartOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            xAxes: [
                {
                    gridLines: { display: true },
                    ticks: { autoSkip: true, maxTicksLimit: 8 },
                },
            ],
            yAxes: [
                {
                    id: 'y-axis-requests',
                    position: 'left',
                    gridLines: { display: true },
                    scaleLabel: { display: true, labelString: 'Requests' },
                    ticks: { beginAtZero: true },
                },
                {
                    id: 'y-axis-sessions',
                    position: 'right',
                    gridLines: { display: false },
                    scaleLabel: { display: true, labelString: 'Sessions' },
                    ticks: { beginAtZero: true },
                },
            ],
        },
        legend: { display: true, position: 'top' },
        tooltips: { mode: 'index', intersect: false },
    };
    usageLineChartColors: Color[] = [
        {
            borderColor: '#3f51b5',
            backgroundColor: 'rgba(63, 81, 181, 0.1)',
            pointBackgroundColor: '#3f51b5',
            pointBorderColor: '#fff',
        },
        {
            borderColor: '#ff4081',
            backgroundColor: 'rgba(255, 64, 129, 0.1)',
            pointBackgroundColor: '#ff4081',
            pointBorderColor: '#fff',
        },
    ];
    usageLineChartType: ChartType = 'line';

    // Pie Chart - Session Length Distribution
    sessionPieChartLabels: Label[] = [];
    sessionPieChartData: number[] = [];
    sessionPieChartType: ChartType = 'pie';
    sessionPieChartOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'right' },
        tooltips: {
            callbacks: {
                label: (tooltipItem, data) => {
                    const dataset = data.datasets[tooltipItem.datasetIndex];
                    const total = (dataset.data as number[]).reduce((acc: number, val: number) => acc + val, 0);
                    const value = dataset.data[tooltipItem.index] as number;
                    const percentage = ((value / total) * 100).toFixed(1);
                    return `${data.labels[tooltipItem.index]}: ${value} (${percentage}%)`;
                },
            },
        },
    };
    sessionPieChartColors: Color[] = [
        {
            backgroundColor: ['rgba(63, 81, 181, 0.8)', 'rgba(255, 64, 129, 0.8)', 'rgba(76, 175, 80, 0.8)', 'rgba(255, 152, 0, 0.8)', 'rgba(156, 39, 176, 0.8)'],
        },
    ];

    // Bar Chart - Performance by Agent
    performanceBarChartData: ChartDataSets[] = [];
    performanceBarChartLabels: Label[] = [];
    performanceBarChartType: ChartType = 'bar';
    performanceBarChartOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [
                {
                    ticks: { beginAtZero: true },
                    scaleLabel: { display: true, labelString: 'Response Time (ms)' },
                },
            ],
        },
        legend: { display: true, position: 'top' },
        tooltips: { mode: 'index', intersect: false },
    };
    performanceBarChartColors: Color[] = [{ backgroundColor: 'rgba(63, 81, 181, 0.7)' }];

    // Horizontal Bar Chart - Feedback by Agent
    feedbackBarChartData: ChartDataSets[] = [];
    feedbackBarChartLabels: Label[] = [];
    feedbackBarChartType: ChartType = 'horizontalBar';
    feedbackBarChartOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            xAxes: [
                {
                    stacked: true,
                    ticks: { beginAtZero: true },
                },
            ],
            yAxes: [
                {
                    stacked: true,
                },
            ],
        },
        legend: { display: true, position: 'top' },
        tooltips: { mode: 'index', intersect: false },
    };
    feedbackBarChartColors: Color[] = [{ backgroundColor: 'rgba(76, 175, 80, 0.8)' }, { backgroundColor: 'rgba(244, 67, 54, 0.8)' }];

    constructor(private organisationService: OrganisationService, private analyticsService: CollectsAiAnalyticsBase, private snackBar: MatSnackBar) {}

    ngOnInit(): void {
        this.loadOrganisations();
    }

    loadOrganisations(): void {
        this.isLoadingOrgs = true;
        this.organisationService
            .getOrganisations()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (orgs) => {
                    this.organisations = orgs;
                    this.isLoadingOrgs = false;
                },
                (error) => {
                    console.error('Error loading organisations', error);
                    this.snackBar.open('Error loading organisations', 'Close', {
                        duration: 3000,
                    });
                    this.isLoadingOrgs = false;
                },
            );
    }

    fetchAnalytics(): void {
        if (!this.selectedOrg.value) {
            this.snackBar.open('Please select an organisation', 'Close', {
                duration: 3000,
            });
            return;
        }

        this.isLoading = true;

        const orgTag = this.selectedOrg.value;
        const startDate = this.dateRange.value.start ? this.formatDate(this.dateRange.value.start) : undefined;
        const endDate = this.dateRange.value.end ? this.formatDate(this.dateRange.value.end) : undefined;

        forkJoin({
            usage: this.analyticsService.getUsageAnalytics(orgTag, startDate, endDate),
            sessions: this.analyticsService.getSessionAnalytics(orgTag, startDate, endDate),
            performance: this.analyticsService.getPerformanceAnalytics(orgTag, startDate, endDate),
            feedback: this.analyticsService.getFeedbackAnalytics(orgTag, startDate, endDate),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (result) => {
                    this.usageData = result.usage;
                    this.sessionData = result.sessions;
                    this.performanceData = result.performance;
                    this.feedbackData = result.feedback;

                    this.updateUsageChart();
                    this.updateSessionChart();
                    this.updatePerformanceChart();
                    this.updateFeedbackChart();

                    this.isLoading = false;
                    this.dataLoaded = true;
                },
                (error) => {
                    console.error('Error loading analytics', error);
                    this.snackBar.open('Error loading analytics data', 'Close', {
                        duration: 3000,
                    });
                    this.isLoading = false;
                },
            );
    }

    updateUsageChart(): void {
        if (!this.usageData?.daily_breakdown) {
            return;
        }
        this.usageLineChartLabels = this.usageData.daily_breakdown.map((d) => this.formatDateLabel(d.date));
        this.usageLineChartData = [
            {
                data: this.usageData.daily_breakdown.map((d) => d.requests),
                label: 'Requests',
                yAxisID: 'y-axis-requests',
                fill: true,
            },
            {
                data: this.usageData.daily_breakdown.map((d) => d.sessions),
                label: 'Sessions',
                yAxisID: 'y-axis-sessions',
                fill: true,
            },
        ];
    }

    updateSessionChart(): void {
        if (!this.sessionData?.session_length_distribution) {
            return;
        }
        const dist = this.sessionData.session_length_distribution;
        this.sessionPieChartLabels = Object.keys(dist).map((key) => this.formatSessionLabel(key));
        this.sessionPieChartData = Object.values(dist);
    }

    updatePerformanceChart(): void {
        if (!this.performanceData?.by_agent) {
            return;
        }
        const agents = Object.entries(this.performanceData.by_agent);
        this.performanceBarChartLabels = agents.map(([name]) => name);
        this.performanceBarChartData = [
            {
                data: agents.map(([, perf]) => perf.avg_ms),
                label: 'Avg Response Time (ms)',
            },
        ];
    }

    updateFeedbackChart(): void {
        if (!this.feedbackData?.by_agent) {
            return;
        }
        const agents = Object.entries(this.feedbackData.by_agent);
        this.feedbackBarChartLabels = agents.map(([name]) => name);
        this.feedbackBarChartData = [
            {
                data: agents.map(([, fb]) => fb.positive),
                label: 'Positive',
            },
            {
                data: agents.map(([, fb]) => fb.negative),
                label: 'Negative',
            },
        ];
    }

    resetFilters(): void {
        this.selectedOrg.reset();
        this.dateRange.reset();
        this.dataLoaded = false;
        this.usageData = null;
        this.sessionData = null;
        this.performanceData = null;
        this.feedbackData = null;
        this.usageLineChartData = [];
        this.sessionPieChartData = [];
        this.performanceBarChartData = [];
        this.feedbackBarChartData = [];
    }

    private formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private formatDateLabel(dateStr: string): string {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    private formatSessionLabel(key: string): string {
        return key.replace(/_/g, ' ').replace(/messages?/gi, 'msg');
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
